import { CommonModule, DOCUMENT } from "@angular/common";
import { ChangeDetectionStrategy, Component, computed, effect, inject, input, signal } from "@angular/core";
import { toSignal } from "@angular/core/rxjs-interop";
import { ActivatedRoute, Params, Router, RouterModule } from "@angular/router";
import { TranslateModule, TranslateService } from "@ngx-translate/core";
import { injectQuery } from "@tanstack/angular-query-experimental";
import Fuse from "fuse.js";
import { AutoCompleteModule } from "primeng/autocomplete";
import { TooltipModule } from "primeng/tooltip";

import { injectOpenActionOverlay } from "@intocare/angular-routing/lib/overlay-navigation";
import { PermissionsService } from "@intocare/auth-data";
import { PageFilterQueryParam, PageFilterService, PageFilterType } from "@intocare/client-side-state";
import { bedTypes } from "@intocare/core";
import { CareDataService } from "@intocare/data-access/portal/care";
import { DADepartmentWithRooms } from "@intocare/data-access/portal/organisation";
import { IconButtonAction, TagActionsButtonComponent, TagComponent } from "@intocare/design/design-system-base";
import { Presence } from "@intocare/interfaces";
import { LanguageModule, LanguagePipe } from "@intocare/language";
import { AgePipe, NamePipe } from "@intocare/pipes";
import { FinalizedBaseResident } from "@intocare/residents/data";
import {
	RESIDENT_OPTION_NAME_ORDER,
	residentOptionsSorting,
	ResidentsSharedUIModule,
} from "@intocare/residents/shared-ui";
import { CONTEXT_TOKEN } from "@intocare/util";

import { DepartmentListItemComponent } from "./department-list-item.component";
import { PageFilterControlComponent } from "./page-filter-control.component";
import { UserHome } from "../../entities";
import { MedicalInfoModel } from "../../models";
import { RoomPipe } from "../../pipes";

interface DepartmentSuggestion {
	id: string;
	name: string;
	isOccupationEmpty: boolean;
}

interface ResidentSuggestion {
	items: FinalizedBaseResident[];
	value: string;
	label: string;
}

@Component({
	selector: "ic-page-filter",
	imports: [
		AgePipe,
		AutoCompleteModule,
		CommonModule,
		DepartmentListItemComponent,
		LanguageModule,
		NamePipe,
		PageFilterControlComponent,
		ResidentsSharedUIModule,
		RoomPipe,
		RouterModule,
		TooltipModule,
		TranslateModule,
		TagComponent,
		TagActionsButtonComponent,
	],
	templateUrl: "./page-filter.component.html",
	styleUrl: "./page-filter.component.scss",
	changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PageFilterComponent {
	private readonly activatedRoute = inject(ActivatedRoute);
	private readonly router = inject(Router);
	protected readonly permissionsService = inject(PermissionsService);
	private readonly medicalInfoModel = inject(MedicalInfoModel);
	private readonly translateService = inject(TranslateService);
	private readonly languagePipe = inject(LanguagePipe);
	private readonly namePipe = inject(NamePipe);
	private readonly pageFilterService = inject(PageFilterService);
	private readonly document = inject(DOCUMENT);

	homes = input.required<UserHome[]>();
	departments = input.required<DADepartmentWithRooms[]>();
	residents = input<FinalizedBaseResident[]>();

	protected homesQuery = signal<string>("");
	protected departmentsQuery = signal<string>("");
	protected residentsQuery = signal<string>("");

	protected filterConfigSignal = this.pageFilterService.getFilterConfigSignal();
	protected PageFilterType = PageFilterType;

	protected homeSuggestions = computed(() => {
		const homesQuery = this.homesQuery();
		return this.getHomeSuggestionsFiltered(homesQuery);
	});

	protected departmentSuggestions = computed(() => {
		const departmentsQuery = this.departmentsQuery();
		return this.getDepartmentSuggestionsFiltered(departmentsQuery);
	});

	protected residentSuggestions = computed(() => {
		const residentsQuery = this.residentsQuery();
		return this.getResidentSuggestionsFiltered(residentsQuery);
	});

	private readonly PARAM_ORGANISATION = "org";
	private readonly PARAM_HOME = "home";

	private readonly baseSearchConfig = {
		isCaseSensitive: false,
		ignoreDiacritics: true,
		ignoreLocation: true,
		minMatchCharLength: 1,
		location: 0,
		threshold: 0.3,
		distance: 2,
	};

	private readonly careDataService = inject(CareDataService);
	private readonly contextService = inject(CONTEXT_TOKEN);
	private readonly openActionOverlay = injectOpenActionOverlay();

	private readonly paramMap = toSignal(this.activatedRoute.paramMap);
	private readonly queryParamMap = toSignal(this.activatedRoute.queryParamMap);
	private readonly loadMetaDataOnResidentChanges = effect(() => {
		const hasDepartments = Boolean(this.departments());
		const hasResidents = Boolean(this.residents());

		if (hasDepartments && hasResidents) {
			const residentId = this.queryParamMap().get(PageFilterQueryParam.NAME_RESIDENT);
			if (residentId) {
				this.medicalInfoModel.loadMedicalInfo(residentId);
			}
		}
	});

	protected readonly restrictiveMeasureButtonAction: IconButtonAction[] = [
		{
			icon: "GO_FORWARD",
			tooltip: this.translateService.instant("pageFilter.toRestrictiveMeasures"),
			command: () =>
				void this.openActionOverlay("aside", "view-restrictive-measures", ["residents", this.selectedResidentId()]),
		},
	];

	private readonly restrictiveMeasuresQuery = injectQuery(() => {
		const residentId = this.selectedResidentId();
		return {
			...this.careDataService.restrictiveMeasuresForResidentQueryOptions(
				{ siteId: this.contextService.context().currentSite.id },
				{ residentId: residentId ?? "" },
			),
			enabled: !!residentId,
		};
	});

	protected hasRestrictiveMeasures = computed(
		() => Object.keys(this.restrictiveMeasuresQuery.data()?.data ?? {}).length > 0,
	);

	protected readonly hasActiveRestrictiveMeasures = computed(
		() => (this.restrictiveMeasuresQuery.data()?.data.ACTIVE?.length ?? 0) > 0,
	);

	private readonly residentsByRoomByDepartment = computed(() => {
		const residents = this.residents();
		if (!residents) {
			return undefined;
		}
		return this.departments()?.map((departmentWithRooms) => ({
			...departmentWithRooms,
			rooms: departmentWithRooms.rooms.map((room) => ({
				...room,
				residents: residents.filter((resident) => resident.room === room.id),
			})),
		}));
	});

	private readonly homeFuse = computed(() => {
		const homes = this.homes();
		return homes ? new Fuse(homes, { ...this.baseSearchConfig, keys: ["name"] }) : undefined;
	});

	private readonly departmentFuse = computed(() => {
		const departments = this.departmentOptions();
		return departments ? new Fuse(departments, { ...this.baseSearchConfig, keys: ["name"] }) : undefined;
	});

	private readonly residentFusesByGroup = computed(() => {
		const groupedResidents = this.residentOptionsGroupedByDepartment();
		return groupedResidents.map((group) => ({
			groupValue: group.value,
			fuse: new Fuse(group.items, { ...this.baseSearchConfig, keys: ["firstName", "name", "fullName"] }),
		}));
	});

	private readonly roomFusesByGroup = computed(() => {
		const groupedResidents = this.residentOptionsGroupedByDepartment();
		return groupedResidents.map((group) => ({
			groupValue: group.value,
			fuse: new Fuse(group.items, { ...this.baseSearchConfig, keys: ["room"] }),
		}));
	});

	// Home filter control
	protected readonly homeOptions = this.homes;
	protected readonly selectedHomeId = computed(() => {
		const paramMap = this.paramMap();
		const org = paramMap.get(this.PARAM_ORGANISATION);
		const home = paramMap.get(this.PARAM_HOME);
		if (!org || !home) {
			return undefined;
		}
		return [org, home].join(".");
	});
	protected readonly selectedHomeOption = computed(() => {
		const homes = this.homes();
		const homeId = this.selectedHomeId();
		return homes?.find((home) => home.id === homeId) || null;
	});
	protected readonly selectedHomeOptionLabelFn = (home: UserHome) => home.name;
	protected readonly isHomeReadonly = computed(() => {
		const homes = this.homes();
		return homes?.length === 1 && this.selectedHomeId() === homes[0].id;
	});

	// Department filter control
	protected readonly departmentOptions = computed(() => {
		const residents = this.residents();
		const departments = this.departments();
		if (!departments || !residents) {
			return [];
		}
		const options = departments
			.map((department) => ({
				id: department.id,
				name: department.display,
				isOccupationEmpty:
					residents?.filter((resident) => department.rooms.map((room) => room.id).includes(resident.room))?.length
					=== 0,
			}))
			.sort((a, b) => a.name.localeCompare(b.name));
		options.unshift({
			id: undefined,
			name: this.translateService.instant("pageFilter.department.all"),
			isOccupationEmpty: false,
		});
		return options;
	});
	protected readonly selectedDepartmentId = computed(() => {
		const paramMap = this.queryParamMap();
		const residentParamValue = paramMap.get(PageFilterQueryParam.NAME_RESIDENT);
		const departmentParamValue = paramMap.get(PageFilterQueryParam.NAME_DEPARTMENT);
		return residentParamValue
			? this.mapResidentIdToDepartmentId(residentParamValue)
			: (departmentParamValue ?? undefined);
	});
	protected readonly selectedDepartmentOption = computed(() => {
		const options = this.departmentOptions();
		const departmentId = this.selectedDepartmentId();
		return options?.find((dept) => dept.id === departmentId) || null;
	});
	protected readonly selectedDepartmentOptionLabelFn = (department: { id: string; name: string }) => department.name;

	// Resident filter control
	protected readonly residentOptions = computed(() => {
		const residents = this.residents();
		if (!residents) {
			return [];
		}
		const options = residents.map((resident) => {
			const residentDepartment = this.departments()?.find((department) =>
				department.rooms.map((room) => room.id).includes(resident.room),
			);

			const rooms = this.departments()?.flatMap((department) => department.rooms);
			const residentRoom = rooms?.find((room) => room.id === resident.room)?.name || "";

			return {
				...resident,
				room: ([Presence.P, Presence.A].includes(resident.presence?.presence) && residentRoom) || undefined,
				department: residentDepartment
					? { id: residentDepartment.id, name: this.languagePipe.transform(residentDepartment.name) }
					: undefined,
			};
		});
		return options.sort(residentOptionsSorting);
	});
	protected readonly residentOptionsGroupedByDepartment = computed(() => {
		const residentOptions = this.residentOptions();
		const filterConfig = this.filterConfigSignal();
		if (!residentOptions || !residentOptions.length) {
			return [];
		}
		const departmentId = this.selectedDepartmentId();
		if (!departmentId) {
			return [
				{
					value: "all",
					label: this.translateService.instant("pageFilter.department.otherGroup"),
					items: [
						...(filterConfig.residentFilter !== PageFilterType.REQUIRED
							? [
									{
										id: undefined,
										department: undefined,
										room: undefined,
										name: this.translateService.instant("pageFilter.resident.all"),
									},
								]
							: []),
						...residentOptions,
					],
				},
			];
		}
		return [
			{
				value: departmentId,
				label: this.translateService.instant("pageFilter.department.currentGroup"),
				items: [
					...(filterConfig.residentFilter !== PageFilterType.REQUIRED
						? [
								{
									id: undefined,
									department: undefined,
									room: undefined,
									name: this.translateService.instant("pageFilter.resident.all"),
								},
							]
						: []),
					...residentOptions
						.filter((resident) => resident.department?.id === departmentId)
						.sort(residentOptionsSorting),
				],
			},
			{
				value: "all",
				label: this.translateService.instant("pageFilter.department.otherGroup"),
				items: residentOptions
					.filter((resident) => resident.department?.id !== departmentId)
					.sort(residentOptionsSorting),
			},
		];
	});
	protected readonly selectedResidentId = computed(() => {
		const paramMap = this.queryParamMap();
		return paramMap.get(PageFilterQueryParam.NAME_RESIDENT);
	});
	protected readonly residentOptionNameOrder = RESIDENT_OPTION_NAME_ORDER;
	protected readonly selectedResidentOption = computed(() => {
		const selectedResidentId = this.selectedResidentId();
		if (!selectedResidentId && this.filterConfigSignal().residentFilter !== PageFilterType.REQUIRED) {
			return {
				id: undefined,
				name: this.translateService.instant("pageFilter.resident.all"),
				room: undefined,
				department: undefined,
			};
		}
		return this.residentOptions().find((resident) => resident.id === selectedResidentId) || null;
	});
	protected readonly selectedResidentOptionLabelFn = (resident: FinalizedBaseResident) =>
		this.namePipe.transform(resident);
	protected readonly isResidentReadonly = computed(() => {
		const options = this.residentOptions();
		return options?.length === 1 && this.selectedResidentId() === options[0].id;
	});

	// Selected resident display
	protected readonly medicalInfo = toSignal(this.medicalInfoModel.medicalInfo$);
	protected readonly presencesWithoutRoomDisplay = [Presence.D, Presence.L];
	protected readonly presencePresent = Presence.P;
	protected readonly selectedResident = computed(() => {
		const selectedResidentId = this.selectedResidentId();
		if (!selectedResidentId) {
			return null;
		}
		const selectedResidentOption = this.residentOptions().find((resident) => resident.id === selectedResidentId);
		return selectedResidentOption ? { ...selectedResidentOption } : undefined;
	});
	protected readonly selectedResidentBedType = computed(() => {
		const selectedResident = this.selectedResident();
		if (!selectedResident) {
			return undefined;
		}
		return bedTypes.find((bedType) => bedType.type === selectedResident.bedType.bedType).name;
	});

	onDepartmentSelected(departmentId: string | undefined): void {
		if (departmentId) {
			this.updateQueryParams({
				[PageFilterQueryParam.NAME_DEPARTMENT]: departmentId,
				[PageFilterQueryParam.NAME_RESIDENT]: undefined,
			});
		} else {
			this.updateQueryParams({
				[PageFilterQueryParam.NAME_DEPARTMENT]: undefined,
				[PageFilterQueryParam.NAME_RESIDENT]: undefined,
			});
		}
	}

	onResidentSelected(residentId: string | undefined): void {
		if (residentId) {
			this.updateQueryParams({
				[PageFilterQueryParam.NAME_RESIDENT]: residentId,
				[PageFilterQueryParam.NAME_DEPARTMENT]: undefined,
			});
		} else {
			const currentResident = this.residents()?.find((resident) => resident.id === this.selectedResidentId());
			if (!currentResident) {
				return;
			}
			const residentDepartment = this.departments()?.find((department) =>
				department.rooms.map((room) => room.id).includes(currentResident.room),
			);

			this.updateQueryParams({
				[PageFilterQueryParam.NAME_RESIDENT]: undefined,
				[PageFilterQueryParam.NAME_DEPARTMENT]: residentDepartment ? residentDepartment.id : undefined,
			});
		}
	}

	onHomeSelected(homeId: string | undefined): void {
		if (homeId) {
			if (!this.document.defaultView) {
				throw new Error("Window object is not available");
			}
			if (homeId !== this.selectedHomeId()) {
				this.document.defaultView.location.href = "/" + homeId.replace(".", "/");
			}
		}
	}

	private updateQueryParams(params: Params): void {
		this.router.navigate([], {
			queryParams: params,
			preserveFragment: true,
		});
	}

	private mapResidentIdToDepartmentId(residentId?: string): string | undefined {
		if (!residentId) {
			return undefined;
		}
		return this.residentsByRoomByDepartment()?.find((department) =>
			department.rooms.find((room) => room.residents.find((resident) => resident.id === residentId)),
		)?.id;
	}

	private getHomeSuggestionsFiltered(query = ""): UserHome[] {
		if (this.isHomeReadonly()) {
			return [];
		}
		const fuse = this.homeFuse();
		return fuse && query ? fuse.search(query, { limit: 15 }).map((result) => result.item) : this.homes() || [];
	}

	private getDepartmentSuggestionsFiltered(query = ""): DepartmentSuggestion[] {
		const fuse = this.departmentFuse();
		return fuse && query
			? fuse.search(query, { limit: 15 }).map((result) => result.item)
			: this.departmentOptions() || [];
	}

	private getResidentSuggestionsFiltered(query = ""): ResidentSuggestion[] | FinalizedBaseResident[] {
		if (this.isResidentReadonly()) {
			return [];
		}

		const fusesByGroup = query.match(/^[,.\d]+$/) ? this.roomFusesByGroup() : this.residentFusesByGroup();

		const groupedFilteredOptions = fusesByGroup
			.map(({ groupValue, fuse }) => {
				const group = this.residentOptionsGroupedByDepartment().find((g) => g.value === groupValue);
				if (!group) {
					return null;
				}
				const filteredItems =
					fuse && query ? fuse.search(query, { limit: 15 }).map((result) => result.item) : group.items;
				return {
					...group,
					items: filteredItems,
				};
			})
			.filter((group) => group && group.items.length);

		return groupedFilteredOptions;
	}
}
